import React, { useId, useState } from 'react';
import {
  parseRoundTypes,
  PromptFormat,
  PromptFormatName,
  RoundTypes,
} from '@shared/constants';

import './round-type-picker.css';

const STORAGE_KEY = 'icebreaks.selectedRoundTypes';

export const loadSelectedRoundTypes = (): PromptFormat[] => {
  try {
    const parsed = parseRoundTypes(JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null'));
    return parsed ?? [...RoundTypes];
  } catch {
    return [...RoundTypes];
  }
};

export const saveSelectedRoundTypes = (formats: PromptFormat[]) => {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(formats));
};

export const useSelectedRoundTypes = () => {
  const [selected, setSelected] = useState<PromptFormat[]>(loadSelectedRoundTypes);

  const toggle = (format: PromptFormat): PromptFormat[] => {
    const next = selected.includes(format)
      ? selected.filter(f => f !== format)
      : [...selected, format];
    saveSelectedRoundTypes(next);
    setSelected(next);
    return next;
  };

  return {
    selected,
    toggle,
    hasAny: selected.length > 0,
  };
};

const RoundBlurb: Record<PromptFormat, string> = {
  [PromptFormat.MULTIPLE_CHOICE]: 'Guess the popular pick',
  [PromptFormat.FREE_INPUT]: 'Spot who wrote it',
  [PromptFormat.SLIDER]: 'Closest average wins',
  [PromptFormat.DRAWING]: 'Sketch, then vote',
};

type RoundTypePickerProps = {
  selected: PromptFormat[];
  onToggle: (format: PromptFormat) => void;
  stacked?: boolean;
};

export const RoundTypePicker = ({
  selected,
  onToggle,
  stacked = false,
}: RoundTypePickerProps) => {
  const uid = useId();
  return (
    <div className={'roundTypePicker' + (stacked ? ' stacked' : '')}>
      <div className='roundTypePickerHeading'>Rounds to include</div>
      <div className='roundTypePickerList'>
        {RoundTypes.map(format => {
          const id = `${uid}-${format}`;
          const checked = selected.includes(format);
          return (
            <div key={format} className='roundTypeOption'>
              <input
                id={id}
                type='checkbox'
                checked={checked}
                onChange={() => onToggle(format)}
              />
              <label htmlFor={id} data-format={format} title={RoundBlurb[format]}>
                {PromptFormatName[format]}
              </label>
            </div>
          );
        })}
      </div>
      {stacked && selected.length === 0 && (
        <p className='roundTypeHint'>Pick at least one round</p>
      )}
    </div>
  );
};
